import React, { useState } from 'react';
import {
  MessageSquare,
  Calendar,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  ListChecks,
  XCircle,
} from 'lucide-react';
import { Button, Input, SearchSelect } from '../../components/shared';
import DataTable from '../../components/shared/DataTable';
import ContactMessageDetailModal from '../../components/admin/ContactMessageDetailModal';
import { useAdminContactMessages } from '../../hooks/useAdminContactMessages';
import { formatDate, formatTime } from '../../utils/dateUtils';
import { useToastContext } from '../../contexts/ToastContext';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminContactMessages = () => {
  const toast = useToastContext();
  const [selectedMessage, setSelectedMessage] = useState(null);
  const {
    messages,
    isLoading,
    error,
    filters,
    meta,
    statistics,
    handleFilterChange,
    handlePageChange,
    updateMessage,
    deleteMessage,
    replyMessage,
    isReplying,
    INQUIRY_LABELS,
    STATUS_LABELS,
  } = useAdminContactMessages();

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'bg-sky-50 text-sky-800 dark:bg-sky-950/40 dark:text-sky-300';
      case 'read':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
      case 'replied':
        return 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'From',
      sortable: false,
      width: 'w-48',
      render: (msg) => (
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{msg.name}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{msg.email}</p>
        </div>
      ),
    },
    {
      key: 'subject',
      label: 'Subject',
      sortable: false,
      width: 'w-48',
      render: (msg) => (
        <p className="text-sm font-medium text-gray-900 dark:text-gray-50 truncate">{msg.subject}</p>
      ),
    },
    {
      key: 'inquiry_type',
      label: 'Type',
      sortable: false,
      width: 'w-32',
      render: (msg) => (
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {INQUIRY_LABELS[msg.inquiry_type] || msg.inquiry_type}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: false,
      width: 'w-24',
      render: (msg) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(msg.status)}`}>
          {STATUS_LABELS[msg.status] || msg.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: false,
      width: 'w-32',
      render: (msg) => (
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium text-gray-900 dark:text-gray-50">{formatDate(msg.created_at, 'medium')}</div>
            <div className="text-xs text-gray-500 dark:text-gray-400">{formatTime(msg.created_at)}</div>
          </div>
        </div>
      ),
    },
  ];

  const actions = [
    {
      label: 'View',
      icon: Eye,
      onClick: (msg) => setSelectedMessage(msg),
    },
    {
      label: 'Delete',
      icon: Trash2,
      onClick: (msg) => {
        if (window.confirm('Are you sure you want to delete this message?')) {
          deleteMessage(msg.id);
          toast.success(null, 'Message deleted');
          if (selectedMessage?.id === msg.id) setSelectedMessage(null);
        }
      },
      className: 'text-red-600 hover:text-red-700',
    },
  ];

  if (error) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error loading contact messages</h3>
          <p className="text-red-600 dark:text-red-300 text-sm">
            {error?.response?.data?.message || error?.message || 'Failed to load messages.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Contact Messages"
        subtitle="Messages submitted via the Contact Us form."
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.contactMessages.title}
        steps={ADMIN_PAGE_GUIDES.contactMessages.steps}
        icon={ListChecks}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">Total</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{statistics.total}</p>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <p className="text-sm text-sky-600 dark:text-sky-400">New</p>
          <p className="text-2xl font-bold text-sky-700 dark:text-sky-300 mt-1 tabular-nums">{statistics.new}</p>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">Read</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-50 mt-1 tabular-nums">{statistics.read}</p>
        </div>
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-5">
          <p className="text-sm text-emerald-600 dark:text-emerald-400">Replied</p>
          <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-1 tabular-nums">{statistics.replied}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 space-y-5">
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-950/40 p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="flex-1 min-w-[200px]">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                <Input
                  placeholder="Search name, email, subject..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full !rounded-xl"
                />
              </div>
              <div className="w-40">
                <SearchSelect
                  label="Status"
                  options={[
                    { value: '', label: 'All' },
                    { value: 'new', label: 'New' },
                    { value: 'read', label: 'Read' },
                    { value: 'replied', label: 'Replied' },
                  ]}
                  value={filters.status || ''}
                  onChange={(val) => handleFilterChange('status', val)}
                  placeholder="All"
                  searchable={false}
                  clearable
                />
              </div>
              <div className="w-48">
                <SearchSelect
                  label="Inquiry Type"
                  options={[
                    { value: '', label: 'All' },
                    { value: 'general', label: 'General' },
                    { value: 'booking', label: 'Booking' },
                    { value: 'technical', label: 'Technical' },
                    { value: 'vendor', label: 'Vendor' },
                  ]}
                  value={filters.inquiry_type || ''}
                  onChange={(val) => handleFilterChange('inquiry_type', val)}
                  placeholder="All"
                  searchable={false}
                  clearable
                />
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading contact messages…</p>
            </div>
          ) : (
            <DataTable
              data={messages}
              columns={columns}
              actions={actions}
              isLoading={false}
              emptyMessage="No contact messages yet"
              className="!border-0 !shadow-none"
            />
          )}

          {meta.last_page > 1 && (
            <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing page {meta.current_page} of {meta.last_page} ({meta.total} total)
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="!rounded-xl"
                  onClick={() => handlePageChange(meta.current_page - 1)}
                  disabled={meta.current_page <= 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="!rounded-xl"
                  onClick={() => handlePageChange(meta.current_page + 1)}
                  disabled={meta.current_page >= meta.last_page}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedMessage && (
        <ContactMessageDetailModal
          message={selectedMessage}
          onClose={() => setSelectedMessage(null)}
          INQUIRY_LABELS={INQUIRY_LABELS}
          updateMessage={updateMessage}
          replyMessage={replyMessage}
          isReplying={isReplying}
          toast={toast}
        />
      )}
    </div>
  );
};

export default AdminContactMessages;
